import type { AxiosInstance } from 'axios'
import type { ICompany, IFactoryFunctionRequest, paginate } from '@bc-library/interface'
import { EMethod } from '@bc-library/interface'
import { API_MAIN_VERSION, BASE_URL } from '@/config/constantes'
import useAxios from '@/services/api/useAxios'
import {
  afterReceivingAdministrationMessages,
  afterReceivingInbox,
  afterReceivingMessages,
  beforeSendingAdministrationMessage,
} from './hooks'

const resource = 'messages'

// Inbox types
export interface InboxItem {
  _id: string
  processId: string
  candidateId: string
  candidate: {
    _id: string
    firstname: string
    lastname: string
    picture: string | null
  }
  position: {
    _id: string
    name: string
  } | null
  currentStatus: number
  latestMessage: {
    _id: string
    _created: string
    Subject: string
    TextBody: string
    isInbound: boolean
  inboundSenderType?: 'candidate' | 'external' | null
    viewBy: { resourceId: string; viewAt: string }[]
  }
  unreadCount: number
}

export interface AttachmentContent {
  Name: string
  Content: string
  ContentType: string
  ContentLength: string
}

export interface Message {
  _id: string
  _created: string
  Subject: string
  TextBody: string
  HtmlBody: string
  From: string
  FromName: string
  To: string
  ToFull: { Email: string; Name: string; MailboxHash: string }[]
  CcFull: { Email: string; Name: string; MailboxHash: string }[]
  ReplyTo: string
  isInbound: boolean
  inboundSenderType?: 'candidate' | 'external' | null
  viewBy: { resourceId: string; viewAt: string }[]
  isBounced: boolean | null
  Attachments: {
    Name: string
    ContentType: string
    ContentLength: string
    ContentID: string
    Content?: string
  }[]
  sentByPopulated: {
    _id: string
    firstname: string
    lastname: string
    picture: string | null
    email: string
  } | null
}

export type AdministrationMailMessage = {
  _id: string
  _created: Date
  sentBy?: {
    _id: string
    firstname: string
    lastname: string
  } | null
  From: string
  FromName: string
  To: string
  label: string
  Subject?: string
  isBounced: boolean | null
  errorCode?: number
  isSeen?: boolean | null
  HtmlBody?: string
  TextBody?: string
  Attachments?: Message['Attachments']
  disabled?: boolean
  parsedMail?: string
}

const interviewsMessages = (axios: IFactoryFunctionRequest<paginate<Array<Pick<ICompany, '_id' | 'display'>>>, undefined>) => (processId: string) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/recruitmentProcess/${processId}/interviews`,
  })
}

// Inbox endpoints
const getInbox = (axios: IFactoryFunctionRequest<paginate<InboxItem>, undefined>) => (query: { page?: number; limit?: number; search?: string; isOpen?: string }) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/inbox`,
    query,
    afterReceivingHook: afterReceivingInbox,
  })
}

const getConversation = (axios: IFactoryFunctionRequest<paginate<Message>, undefined>) => (processId: string, query: { page?: number; limit?: number }) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/inbox/${processId}/conversation`,
    query,
    afterReceivingHook: afterReceivingMessages,
  })
}

const markAsSeen = (axios: IFactoryFunctionRequest<{ success: boolean }, undefined>) => (processId: string) => {
  return axios({
    method: EMethod.put,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/inbox/${processId}/seen`,
  })
}

const markAllAsRead = (axios: IFactoryFunctionRequest<{ success: boolean }, undefined>) => () => {
  return axios({
    method: EMethod.put,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/inbox/mark-all-read`,
  })
}

const getUnreadCount = (axios: IFactoryFunctionRequest<{ unreadCount: number }, undefined>) => () => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/inbox/unread-count`,
  })
}

const getAttachment = (axios: IFactoryFunctionRequest<AttachmentContent, undefined>) => (messageId: string, attachmentIndex: number) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/inbox/${messageId}/attachment/${attachmentIndex}`,
  })
}

const getFirmMessages = (axios: IFactoryFunctionRequest<AdministrationMailMessage[], undefined>) => (
  firmId: string,
  page: number,
  limit: number,
  query: { search?: string, labels?: string[] } = {},
) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/firm/${firmId}/${page}/${limit}`,
    query,
    afterReceivingHook: afterReceivingAdministrationMessages,
  })
}

const getBouncedMessages = (axios: IFactoryFunctionRequest<AdministrationMailMessage[], undefined>) => (firmId: string) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/getBounced/${firmId}`,
    afterReceivingHook: afterReceivingAdministrationMessages,
  })
}

const getBouncedCount = (axios: IFactoryFunctionRequest<{ count: number }, undefined>) => (firmId: string) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/errors/${firmId}`,
  })
}

const updateMessage = (axios: IFactoryFunctionRequest<AdministrationMailMessage, Partial<AdministrationMailMessage>>) => (id: string, data: Partial<AdministrationMailMessage>) => {
  return axios({
    method: EMethod.put,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/${id}`,
    data,
    beforeSendingHook: beforeSendingAdministrationMessage,
    afterReceivingHook: afterReceivingAdministrationMessages,
  })
}

const reactivateRecipient = (axios: IFactoryFunctionRequest<void, undefined>) => (email: string) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/reactivate/${encodeURIComponent(email)}`,
  })
}

const getMessageById = (axios: IFactoryFunctionRequest<AdministrationMailMessage, undefined>) => (id: string) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/${id}`,
    afterReceivingHook: afterReceivingAdministrationMessages,
  })
}

export default (axios: AxiosInstance) => ({
  interviewsMessages: interviewsMessages(useAxios(axios)),
  getInbox: getInbox(useAxios(axios)),
  getConversation: getConversation(useAxios(axios)),
  markAsSeen: markAsSeen(useAxios(axios)),
  markAllAsRead: markAllAsRead(useAxios(axios)),
  getUnreadCount: getUnreadCount(useAxios(axios)),
  getAttachment: getAttachment(useAxios(axios)),
  getFirmMessages: getFirmMessages(useAxios(axios)),
  getBouncedMessages: getBouncedMessages(useAxios(axios)),
  getBouncedCount: getBouncedCount(useAxios(axios)),
  updateMessage: updateMessage(useAxios(axios)),
  reactivateRecipient: reactivateRecipient(useAxios(axios)),
  getMessageById: getMessageById(useAxios(axios)),
})
